//Imports
import React, { useCallback, useEffect, useMemo, useState } from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useRealtimeDrivers } from 'Core/hooks';
import { useIntl } from "react-intl";

//Actions
import {
  manageCreateEditManageKeyFob,
  reset,
} from "./redux/slice";

//Controls
import {
  Button,
  Form,
  Input,
  Modal,
  Select,
} from "@gpstrackit/gtc-react-controls";

//Components
import {
  NotificationManager,
  NotificationContainer,
} from "Components/Notifications";
import { Alert } from "@mui/material";

//Helpers
import { hasEmojis, noEmojisValue } from "../../../../../.common/utils/formValues";
import { getRequiredTrimmedNonEmptyRules } from "Modules/.common/utils/gtcFormItemRules";

const CreateEditManageKeyFob = ({ refresh }) => {
  //States
  const [formData, setFormData] = useState({ driver: null, hex: null });

  //Language
  const { messages } = useIntl();

  //Form refs
  const createEditKeyFobFormRef = Form.useForm();

  //Redux
  const keyFob = useSelector((state) => state.manageCreateEditManageKeyFobRedux.keyFob);
  let loading = useSelector((state) => state.manageCreateEditManageKeyFobRedux.loading);
  const error = useSelector((state) => state.manageCreateEditManageKeyFobRedux.error);
  const updateManageKeyFobResult = useSelector((state) => state.manageCreateEditManageKeyFobRedux.updateManageKeyFobResult);
  const open = useSelector((state) => state.manageCreateEditManageKeyFobRedux.open);

  //get the information for drivers from catalog
  const allDrivers = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });

  const drivers = useMemo(() => {
    return allDrivers?.map(driver => ({ id: driver.id, title: driver.title || driver.name }));
  }, [allDrivers]);
  
  const loadingDrivers = useSelector(state => state.catalogsRealtimeRedux.loadingDrivers);
  loading = loading || loadingDrivers;

  //Memos
  const title = useMemo(
    () =>
      keyFob?.hex
        ? `${messages["manage_createEditKeyFob_EditTitle"]}: "${keyFob?.hex}"`
        : messages["manage_createEditKeyFob_CreateTitle"],
    [keyFob]
  );

  const hexFieldRules = useMemo(
    () => getRequiredTrimmedNonEmptyRules(messages["required_field"]),
    [messages]
  );
  const dispatch = useDispatch();

  //Handlers and utils
  const handleUpdateManageKeyFobResult = useCallback(() => {
    const hex = formData.hex != null ? String(formData.hex).trim() : "";
    if (!hex) return;
    const { driver } = formData;
    dispatch(
      manageCreateEditManageKeyFob({
        hex,
        driverId: driver?.id || 0,
        id: keyFob?.id,
      })
    );
  }, [formData, keyFob]);

  const handleClose = useCallback(() => {
    dispatch(reset());
  }, []);

  const handleSuccess = useCallback(() => {
    NotificationManager.success(
      keyFob?.id ? messages["manage_createEditKeyFob_success_edit"] : messages["manage_createEditKeyFob_success"],
      messages["success"],
      3000,
      null,
      null,
      "success"
    );
    refresh();
    handleClose();
  }, []);

  //Effects
  useEffect(() => {
    if (!updateManageKeyFobResult?.id) return;
    handleSuccess();
  }, [updateManageKeyFobResult]);

  useEffect(() => {
    if (!error) return;
    let errorMessage = error?.error || messages["errorMessage"];
    if (
      error?.errorData?.errors?.find((err) => err?.errorInfo?.code === 1010)
    ) {
      errorMessage =
        messages["manage_createEditKeyFob_duplicatedKeyFobErrorMessage"];
    }
    NotificationManager.error(
      errorMessage,
      messages["error"],
      4000,
      null,
      null,
      "error"
    );
    dispatch(reset({ error: null }));
  }, [error]);

  useEffect(() => {
    if (!open || !keyFob) return;
    const { hex, driverId } = keyFob;

    const defaultData = {
      hex,
      driver: drivers?.find((driver) => driver?.id === driverId + ""),
    };
    setFormData(defaultData);
    setTimeout(() => {
      createEditKeyFobFormRef.setValues(defaultData);
    }, 600)
  }, [keyFob, open, drivers]);

  useEffect(() => {
    const value = formData.hex;
    if (!value) return;
    if (!hasEmojis(value)) return;
    const newFormData = { ...formData, hex: noEmojisValue(value) };
    setFormData(newFormData);

    setTimeout(() => {
      createEditKeyFobFormRef.setValues(newFormData);
    }, 500)
  }, [formData]);

  return (
    <Modal
      title={
        title && title.length > 48 ? title.substring(0, 45) + '..."' : title
      }
      open={open}
      handleClose={handleClose}
      position="right"
      style={{ zIndex: "auto" }}
      size="small"
      loading={loading}
      footer={
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Button
            isDisabled={loading}
            style={{ marginRight: 8 }}
            onClick={handleClose}
            variant="secondary"
          >
            {messages["cancel"]}
          </Button>

          <Button
            isDisabled={loading}
            onClick={() => {
              createEditKeyFobFormRef?.submit();
            }}
            variant="primary"
          >
            {messages["save"]}
          </Button>
        </div>
      }
    >
      <div style={{ padding: 24 }}>
        {keyFob?.keyFobModelId === 2 && (
          <Alert severity="warning">
            {messages["manage_createEditKeyFob_infoBluetooth"]}
          </Alert>
        )}
        <Form
          ref={createEditKeyFobFormRef.ref}
          loading={loading}
          onValidSubmit={() => handleUpdateManageKeyFobResult()}
          onChangeValues={() => { }}
        >
          <Form.Item
            name="hex"
            label={messages["manage_createEditKeyFob_HexInputLabel"]}
            rules={hexFieldRules}
          >
            <Input
              id="hex"
              type="text"
              placeholder={
                messages["manage_createEditKeyFob_HexInputPlaceholder"]
              }
              onChange={(value) => {
                setFormData((currentFormData) => {
                  const newFormData = { ...currentFormData, hex: value };
                  createEditKeyFobFormRef.setValues(newFormData);
                  return newFormData;
                });
              }}
              maxLength={20}
              isDisabled={keyFob?.id || keyFob?.keyFobModelId === 2}
              value={formData.hex}
            />
          </Form.Item>
          <Select
            id="driver"
            label={messages["manage_createEditKeyFob_DriverInputLabel"]}
            placeholder={
              messages["manage_createEditKeyFob_DriverInputPlaceholder"]
            }
            loading={loading}
            options={drivers || []}
            textField="title"
            valueField="id"
            value={formData.driver?.id ? formData.driver : null}
            onChange={(value) => setFormData({ ...formData, driver: value })}
            isClearable
            isSearchable
            isLoading={loadingDrivers}
          />
        </Form>
      </div>
      <NotificationContainer />
    </Modal>
  );
};

export default CreateEditManageKeyFob;
